/**
 * Shared nav-tour ↔ route sync (web + mobile).
 * Path matching is segment-based so brand path prefixes stay aligned.
 */

export type NavTourRouteStepId =
  | 'home'
  | 'forums'
  | 'groups'
  | 'calendar'
  | 'blog'
  | 'feedback'
  | 'marketplace-enable'
  | 'marketplace'
  | 'theme';

/** Normalize pathname to slash-trimmed segments (no query/hash). */
export function pathSegments(pathname: string | null | undefined): string[] {
  if (!pathname) return [];
  const raw = pathname.split('?')[0].split('#')[0];
  return raw.split('/').filter(Boolean);
}

/**
 * Map a URL path to a nav-tour step id.
 * Enable surfaces: /settings/account (web) and /you (mobile).
 */
export function navTourStepIdFromPathname(pathname: string | null | undefined): NavTourRouteStepId | null {
  const parts = pathSegments(pathname).filter((p) => p !== '(app)' && p !== '(tabs)' && p !== 'tour');
  if (parts.length === 0) return 'home';

  const last = parts[parts.length - 1] ?? '';
  const joined = parts.join('/');

  if (joined.includes('settings/account') || last === 'you' || parts.includes('you')) {
    return 'marketplace-enable';
  }
  if (parts.includes('marketplace')) return 'marketplace';
  if (parts.includes('forum') || parts.includes('forums')) return 'forums';
  if (parts.includes('groups')) return 'groups';
  if (parts.includes('calendar')) return 'calendar';
  if (parts.includes('blog')) return 'blog';
  if (parts.includes('feedback')) return 'feedback';
  if (last === 'home' || last === 'feed' || parts[0] === 'feed') return 'home';
  return null;
}

/** Index of step matching pathname, or null if no match / step not in tour. */
export function navTourStepIndexForPathname(
  pathname: string | null | undefined,
  stepIds: readonly string[],
): number | null {
  const id = navTourStepIdFromPathname(pathname);
  if (!id) return null;
  const idx = stepIds.indexOf(id);
  return idx >= 0 ? idx : null;
}

/**
 * Bootstrap step after remount: prefer the current route (sidebar click),
 * else restored in-progress index.
 *
 * Only keep progress-ahead-of-path for marketplace-enable (forced route push);
 * social Next no longer navigates, so sidebar remounts must follow the URL.
 */
export function resolveNavTourBootstrapStepIndex(
  pathname: string | null | undefined,
  stepIds: readonly string[],
  progressStepIndex: number | null | undefined,
): number {
  const fromPath = navTourStepIndexForPathname(pathname, stepIds);
  const prog =
    typeof progressStepIndex === 'number' && progressStepIndex >= 0
      ? Math.min(progressStepIndex, Math.max(0, stepIds.length - 1))
      : null;

  if (
    prog !== null &&
    fromPath !== null &&
    prog > fromPath &&
    stepIds[prog] === 'marketplace-enable'
  ) {
    return prog;
  }
  if (fromPath !== null) return fromPath;
  if (prog !== null) return prog;
  return 0;
}
